import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { RH_MEDIA } from '../../lib/rhapsodyMedia';

type EngineLayer = {
  name: string;
  tag: string;
  description: string;
};

/** Ordered foundation → top of the stack. Copy kept short — the stack is the hero. */
const LAYERS: EngineLayer[] = [
  {
    name: 'Frontier Video Models',
    tag: 'The muscle',
    description: 'Best-in-class generators, each dispatched to the shot it renders best.',
  },
  {
    name: 'Shot Spec IR',
    tag: 'One grammar',
    description: 'One shared way to describe a shot so nothing is locked to a single tool.',
  },
  {
    name: 'Project Memory Graph',
    tag: 'System of record',
    description: 'A living picture of the story-world that always knows what’s true.',
  },
  {
    name: 'Causal Planner',
    tag: 'Reasoning forward',
    description: 'Every shot is planned in context, so it stays true to the story so far.',
  },
  {
    name: 'Coherence Verifier',
    tag: 'Correctness',
    description: 'Every shot is checked for consistency and craft before it’s allowed through.',
  },
  {
    name: 'Cinematic Constitution',
    tag: 'Craft & taste',
    description: 'A living standard for cinematic craft, tuned by real directorial taste.',
  },
  {
    name: 'The Verified Cut',
    tag: 'What ships',
    description: 'Only verified shots make the cut, and the story-world learns from each one.',
  },
];

const PLATE_GAP = 34;
/* Upper (not-yet-active) plates are lifted this far out of the stack and
   hidden entirely — so they drop in from above as you reach each layer. */
const SPLIT_GAP = 150;
const SECTION_HEIGHT_VH = (LAYERS.length + 1) * 72;

type EntrancePhase = 'idle' | 'hidden' | 'enter' | 'done';

export function RhapsodyFeatures() {
  const sectionRef = useRef<HTMLElement>(null);
  const sceneRef = useRef<HTMLDivElement>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLDivElement>(null);
  const pillsRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef(-1);
  const [entrance, setEntrance] = useState<EntrancePhase>('idle');

  /**
   * Apply the active layer imperatively — toggling classes + the plate --pz
   * on the DOM directly. This keeps scroll out of React's render path entirely,
   * so the pills never re-render while you scroll (the JSX classNames stay
   * constant, so React never touches what we set here).
   */
  const applyActive = useCallback((i: number) => {
    const prev = activeRef.current;
    if (i === prev) return;
    // Fast scroll can jump several layers in one frame. Animating 6 heavy glass
    // plates (opacity + transform) at once is what stutters and renders a
    // half-composited stack — so on any multi-step jump we snap without
    // transitions and let it land instantly. Single steps still animate.
    const snap = prev >= 0 && Math.abs(i - prev) > 1;
    activeRef.current = i;

    const stack = stackRef.current;
    if (snap && stack) stack.setAttribute('data-jump', 'true');

    stack?.querySelectorAll<HTMLElement>('.rh-plate').forEach((el, idx) => {
      el.classList.toggle('on', idx === i);
      el.classList.toggle('done', idx < i);
      el.classList.toggle('above', idx > i);
      el.style.setProperty('--pz', `${idx * PLATE_GAP + (idx > i ? SPLIT_GAP : 0)}px`);
    });

    const pills = pillsRef.current;
    pills?.querySelectorAll<HTMLElement>('.rh-aos-pill').forEach((el, idx) => {
      const on = idx === i;
      el.classList.toggle('on', on);
      el.setAttribute('aria-selected', String(on));
      el.setAttribute('aria-expanded', String(on));
    });
    progressRef.current?.querySelectorAll<HTMLElement>('.seg').forEach((el, idx) => {
      el.classList.toggle('on', idx <= i);
    });

    // When the pills are a horizontal rail (mobile), keep the active chip in view.
    // Scroll the rail itself so the browser cannot adjust the document's vertical
    // scroll position while the initial active state is being applied.
    const activePill = pills?.querySelector<HTMLElement>('.rh-aos-pill.on');
    if (activePill && pills && pills.scrollWidth > pills.clientWidth + 4) {
      const target = activePill.offsetLeft - pills.clientWidth / 2 + activePill.offsetWidth / 2;
      pills.scrollTo({ left: Math.max(0, target), behavior: snap ? 'auto' : 'smooth' });
    }

    if (snap && stack) {
      // Keep the jump state through the next paint so the position changes
      // snap, then re-enable transitions on the following frame. This avoids
      // the old synchronous offsetHeight layout flush.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => stack.removeAttribute('data-jump'));
      });
    }
  }, []);

  // Seed the initial state (also sets each plate's --pz) before first paint.
  useLayoutEffect(() => {
    applyActive(0);
  }, [applyActive]);

  // Scroll drives the active layer — purely imperative, no setState.
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let raf = 0;
    const update = () => {
      raf = 0;
      const rect = section.getBoundingClientRect();
      const scrollable = rect.height - window.innerHeight;
      if (scrollable <= 0) return;
      const progress = Math.min(1, Math.max(0, -rect.top / scrollable));
      applyActive(Math.min(LAYERS.length - 1, Math.floor(progress * LAYERS.length)));
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [applyActive]);

  // Drop-in assembly the first time the stack scrolls into view
  useEffect(() => {
    const stack = stackRef.current;
    if (!stack) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    setEntrance('hidden');
    let timeout = 0;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setEntrance('enter');
          timeout = window.setTimeout(() => setEntrance('done'), 1200);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(stack);

    return () => {
      io.disconnect();
      window.clearTimeout(timeout);
    };
  }, []);

  // Mouse-parallax tilt over the scene (desktop, motion-safe only)
  useEffect(() => {
    const scene = sceneRef.current;
    const tilt = tiltRef.current;
    if (!scene || !tilt) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return;

    let raf = 0;
    const target = { x: 0, y: 0 };
    const cur = { x: 0, y: 0 };

    const loop = () => {
      cur.x += (target.x - cur.x) * 0.08;
      cur.y += (target.y - cur.y) * 0.08;
      tilt.style.transform = `rotateX(${cur.x.toFixed(3)}deg) rotateY(${cur.y.toFixed(3)}deg)`;
      if (Math.abs(cur.x - target.x) > 0.01 || Math.abs(cur.y - target.y) > 0.01) {
        raf = requestAnimationFrame(loop);
      } else {
        raf = 0;
      }
    };
    const kick = () => {
      if (!raf) raf = requestAnimationFrame(loop);
    };

    const onMove = (e: MouseEvent) => {
      const r = scene.getBoundingClientRect();
      const nx = ((e.clientX - r.left) / r.width) * 2 - 1;
      const ny = ((e.clientY - r.top) / r.height) * 2 - 1;
      target.x = -ny * 6;
      target.y = nx * 8;
      kick();
    };
    const onLeave = () => {
      target.x = 0;
      target.y = 0;
      kick();
    };

    scene.addEventListener('mousemove', onMove);
    scene.addEventListener('mouseleave', onLeave);

    return () => {
      scene.removeEventListener('mousemove', onMove);
      scene.removeEventListener('mouseleave', onLeave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const jumpTo = (index: number) => {
    const section = sectionRef.current;
    if (!section) return;
    // Keep this in sync with the section's inline `vh` height below. Avoid
    // reading offsetHeight in the interaction path: the stack may have just
    // mutated styles during a scroll frame, which could force a synchronous
    // layout before the smooth scroll starts.
    const scrollable = window.innerHeight * (SECTION_HEIGHT_VH / 100 - 1);
    window.scrollTo({
      top: section.offsetTop + (scrollable * (index + 0.5)) / LAYERS.length,
      behavior: 'smooth',
    });
  };

  return (
    <section
      className="rh-aos"
      id="engine"
      ref={sectionRef}
      style={{
        height: `${SECTION_HEIGHT_VH}vh`,
        '--plate-face-img': `url('${RH_MEDIA.features.featuresplate}')`,
      } as React.CSSProperties}
    >
      <div className="rh-aos-sticky">
        <div className="rh-aos-head">
          <p className="rh-kicker">Introducing the Rhapsody Engine</p>
          <h2 className="rh-h2">One engine. Every <em>layer of the pipeline.</em></h2>
        </div>

        <div className="rh-aos-body">
          <div className="rh-aos-pills" role="tablist" aria-label="Engine layers" ref={pillsRef}>
            {LAYERS.map((layer, i) => (
              <button
                type="button"
                role="tab"
                aria-selected={i === 0}
                aria-expanded={i === 0}
                className={`rh-aos-pill ${i === 0 ? 'on' : ''}`}
                key={layer.name}
                onClick={() => jumpTo(i)}
              >
                <span className="pill-row">
                  <span className="pill-name">{layer.name}</span>
                  <span className="pill-plus" aria-hidden="true">
                    <svg viewBox="0 0 12 12" width="11" height="11">
                      <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                    </svg>
                  </span>
                </span>
                <span className="pill-panel">
                  <span className="pill-panel-inner">
                    <span className="pill-desc">{layer.description}</span>
                  </span>
                </span>
              </button>
            ))}
          </div>

          <div className="rh-aos-scene" aria-hidden="true" ref={sceneRef}>
            {/* atmosphere is a single cheap coral glow — the old full-screen
                blurred-image layer was dropped to relieve compositor pressure */}
            <div className="rh-aos-glow" />
            <div className="rh-stack-tilt" ref={tiltRef}>
              <div className="rh-stack-float">
                <div className="rh-stack" ref={stackRef} data-entrance={entrance}>
                  <span className="rh-stack-shadow" />
                  {LAYERS.map((layer, i) => (
                    <div
                      className={`rh-plate ${i === 0 ? 'on' : 'above'}`}
                      data-plate={i}
                      key={layer.name}
                      style={{ '--pd': `${i * 70}ms` } as React.CSSProperties}
                    >
                      <span className="plate-body" aria-hidden="true">
                        <i /><i /><i />
                      </span>
                      <span className="plate-face">
                        <span className="plate-headline">
                          <span className="ph-tag">{layer.tag}</span>
                          <span className="ph-name">{layer.name}</span>
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rh-aos-progress" aria-hidden="true" ref={progressRef}>
          {LAYERS.map((layer, i) => (
            <span className={`seg ${i === 0 ? 'on' : ''}`} key={layer.name} />
          ))}
        </div>
      </div>
    </section>
  );
}
